// This file contains the socket functionality for both teachers and students in a live discussion
import { mapGetters } from 'vuex';
import { EventBus } from 'utils/event-bus';
import {
  enterPusherChannelAndBindEvents,
  leavePusherChannel,
} from 'utils/realTimeEventHelpers';

// NOTE: These are events for both teacher and student
const general_live_discussion_events = [
  {
    event_name: 'agree_increase',
    handler: ({ user_id, turn_id, participation_id }, context) => {
      // Do not run if the user has already currently agreed
      if (context.users_who_agree.indexOf(user_id) === -1) {
        context.users_who_agree.push(user_id);
        // TODO: Realistically this should be instate if we want to achieve a more decoupled
        // realtime architecture, that way these handlers can essentially just dispatch actions
        // Remove the agree in a pre-determined number of seconds
        context.createDeletionCountDown('users_who_agree', user_id);
      }
      const payload = {
        applauderID: user_id,
        turnID: turn_id,
        participationID: participation_id
      };
      context.$store.commit('APPLAUSE_TURN', payload);
    },
  },

  {
    event_name: 'queue_updated',
    action_name: 'SOCKET_updateQueue',
  },

  {
    event_name: 'teacher_tap_in',
    handler: (payload) => {
      EventBus.$emit('teacher_tap_in', {
        turn_type: payload.turn_type,
        teacher_tapped_in_id: payload.teacher_tapped_in_id
      });
    },
  },

  {
    event_name: 'teacher_tap_out',
    handler: () => {
      EventBus.$emit('teacher_tap_out');
    },
  },

  {
    event_name: 'discussion_ended',
    action_name: 'setLDHasEnded',
  },

  {
    event_name: 'discussion_updated',
    action_name: 'updateLiveDiscussionSpread',
  },

  {
    event_name: 'discussion_restarted',
    action_name: 'setLDHasRestarted',
  },

  {
    event_name: 'new_student_participation',
    action_name: 'addParticipation',
  },

  {
    event_name: 'discussion_started',
    action_name: 'SOCKET_setStartLiveDiscussion',
  },

  {
    event_name: 'discussion_switch_student',
    action_name: 'newTurn',
  },

  {
    event_name: 'discussion_pause_status_update',
    action_name: 'setDiscussionPauseStatus',
  },
  {
    event_name: 'discussion_get_turns',
    action_name: 'getDiscussionTurns'
  },
  {
    event_name: 'add_turn',
    action_name: 'getNewAddTurn'
  },
  {
    event_name: 'remove_turn',
    action_name: 'removeTurn'
  },
  {
    event_name: 'add_turn_queue',
    action_name: 'addTurnQueue'
  },
  {
    event_name: 'remove_turn_queue',
    action_name: 'removeTurnQueue'
  },
  {
    event_name: 'remove_queue_before_turn',
    action_name: 'removeQueueBeforeTurn'
  },
  {
    event_name: 'set_speaker',
    action_name: 'setTurnSpeaker'
  },
  {
    event_name: 'remove_speaker',
    action_name: 'removeTurnSpeaker'
  },
  {
    event_name: 'poll_created',
    handler: (payload, context) => {
      context.$store.dispatch('setPoll', payload);
    },
  },

  {
    event_name: 'poll_deleted',
    handler: (payload, context) => {
      context.$store.dispatch('unsetPoll', payload);
    },
  },

  {
    event_name: 'vote_created',
    handler: (payload, context) => {
      context.$store.dispatch('setVote', payload);
    },
  },

  {
    event_name: 'live_discussion_circle_updated',
    handler: (payload, context) => {
      context.$store.dispatch('updateLiveDiscussionCircle', payload);
    },
  },

  {
    event_name: 'new_turn',
    action_name: 'SOCKET_NewTurn',
  },
  /* {
    event_name: 'remove_turn',
    action_name: 'SOCKET_RemoveTurn',
  }, */

  {
    event_name: 'moderator_chosen',
    action_name: 'SOCKET_moderatorChosen',
  },

  {
    event_name: 'reset_moderator',
    action_name: 'SOCKET_resetModerator',
  },

  {
    event_name: 'toggle_queue',
    action_name: 'SOCKET_toggleQueue',
  },
  {
    event_name: 'toggle_discussion_audio',
    action_name: 'SOCKET_toggleDiscussionAudio',
  },
  {
    event_name: 'toggle_participation_audio',
    action_name: 'SOCKET_toggleParticipationAudio',
  },
  {
    event_name: 'toggle_participation_video',
    action_name: 'SOCKET_toggleParticipationVideo',
  },
  {
    event_name: 'video_off_mute_participations',
    action_name: 'SOCKET_videoOffmuteParticipations'
  },
  {
    event_name: 'mute_participations',
    action_name: 'SOCKET_muteParticipations'
  },
  {
    event_name: 'end_audio_connection',
    action_name: 'SOCKET_endAudioConnection',
  },
  {
    event_name: 'discussion_expired',
    action_name: 'SOCKET_discussionExpired',
  },
];

const teacher_only_live_discussion_events = [
  {
    event_name: 'student_edited_notes',
    action_name: 'updateParticipationSpread',
  },
];

const presence_channel_events = [
  {
    event_name: 'pusher:subscription_succeeded',
    handler: (members, context) => {
      members.each((member) => {
        context.$store.dispatch('addUserToPresenceList', member.id);
      });
    },
  },

  {
    event_name: 'pusher:member_added',
    handler: (member, context) => {
      context.$store.dispatch('addUserToPresenceList', member.id);
    },
  },

  {
    event_name: 'pusher:member_removed',
    handler: async (member, context) => {
      context.$store.dispatch('removeUserFromPresenceList', member.id);
    },
  },
];

export default {
  data() {
    return {
      users_who_agree: [],
      users_who_disagree: [],
      reset_time: 6000,
      pending_timeouts: {},
    };
  },
  computed: {
    ...mapGetters(['hasTeacherPermissionsInCourse']),
  },
  methods: {
    createDeletionCountDown(arrayName, user_id) {
      // Set a timeout in local state under the user_id
      this.pending_timeouts[user_id] = setTimeout(() => {
        const index = this[arrayName].indexOf(user_id);
        if (index !== -1) {
          this[arrayName].splice(index, 1);
        }
      }, this.reset_time);
    },
    getTurn: async function (live_discussion, user_id) {
      let turn_id = '';
      live_discussion.turns_in_circle.forEach((turn) => {
        if (turn.participation.user.id === user_id) {
          turn_id = turn.id;
        }
      });
      return turn_id;
    },
    setupLiveDiscussionSocket(live_discussion_id) {
      enterPusherChannelAndBindEvents.call(
        this,
        live_discussion_id,
        general_live_discussion_events
      );
      enterPusherChannelAndBindEvents.call(
        this,
        `presence-discussion-${live_discussion_id}`,
        presence_channel_events
      );
      if (this.hasTeacherPermissionsInCourse) {
        enterPusherChannelAndBindEvents.call(
          this,
          `teacher-${live_discussion_id}`,
          teacher_only_live_discussion_events
        );
      }
    },
    leaveLiveDiscussionSocket(live_discussion_id) {
      leavePusherChannel(live_discussion_id);
      leavePusherChannel(`teacher-${live_discussion_id}`);
      leavePusherChannel(`presence-discussion-${live_discussion_id}`);
    },
  },
};



// WEBPACK FOOTER //
// ./src/app/mixins/sockets/liveDiscussionSocketEvents.js